import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import KioskHeader from '../../components/KioskHeader/KioskHeader'
import useKioskAutoHome from '../../hooks/useKioskAutoHome'
import {
  Laptop,
  Buildings,
  UsersThree,
  CreditCard,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Ticket,
  Info,
  MapPin,
  Camera,
  WarningCircle,
  UserFocus,
  X,
} from '@phosphor-icons/react'

const CATEGORIES = [
  { key: 'IT Support', desc: 'System, login, device or software issue', icon: Laptop },
  { key: 'Facilities', desc: 'Building, classroom, room or maintenance issue', icon: Buildings },
  { key: 'HR', desc: 'Staff, access, request or admin support', icon: UsersThree },
  { key: 'Finance', desc: 'Payment, billing, claim or invoice issue', icon: CreditCard },
]

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical']

const AREAS = [
  'Kampung Kedup',
  'Serian',
  'Tebedu',
  'Mongkos',
  'Other',
]

export default function SubmitTicketPage() {
  useKioskAutoHome()
  const navigate = useNavigate()
  const videoRef = useRef(null)
  const canvasRef = useRef(null)

  const [step, setStep] = useState(1)
  const [category, setCategory] = useState('')
  const [visitorName, setVisitorName] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [area, setArea] = useState('')
  const [otherArea, setOtherArea] = useState('')
  const [subject, setSubject] = useState('')
  const [priority, setPriority] = useState('Medium')
  const [description, setDescription] = useState('')
  const [visitorPhoto, setVisitorPhoto] = useState(null)
  const [cameraStream, setCameraStream] = useState(null)
  const [cameraError, setCameraError] = useState('')
  const [captureCountdown, setCaptureCountdown] = useState(0)

  const selectedCategory = CATEGORIES.find((item) => item.key === category)

  const canContinueStep1 = Boolean(category)
  const selectedArea = area === 'Other' ? otherArea.trim() : area
  const isPhoneValid = /^1[0-9]{8,9}$/.test(phone) || /^0[0-9]{9,10}$/.test(phone)
  const canContinueStep2 = Boolean(
    visitorName.trim() &&
      isPhoneValid &&
      selectedArea &&
      subject.trim() &&
      description.trim(),
  )
  const canContinueStep3 = Boolean(visitorPhoto)

  const stepLabels = ['Category', 'Details', 'Visitor Photo', 'Review']

  const goToStep = (targetStep) => {
    if (targetStep === 1) setStep(1)
    if (targetStep === 2 && canContinueStep1) setStep(2)
    if (targetStep === 3 && canContinueStep2) setStep(3)
    if (targetStep === 4 && canContinueStep2 && canContinueStep3) setStep(4)
  }


  const handlePhoneChange = (event) => {
    let value = event.target.value.replace(/\D/g, '')

    if (value.length > 11) {
      value = value.slice(0, 11)
    }

    setPhone(value)

    if (!value) {
      setPhoneError('Phone number is required')
    } else if (!(/^1[0-9]{8,9}$/.test(value) || /^0[0-9]{9,10}$/.test(value))) {
      setPhoneError('Enter a valid Malaysia phone number')
    } else {
      setPhoneError('')
    }
  }

  const getFullPhoneNumber = () => {
    if (!phone) return ''

    if (phone.startsWith('0')) {
      return `+6${phone}`
    }

    return `+60${phone}`
  }

  const stopCamera = (stream = cameraStream) => {
    stream?.getTracks?.().forEach((track) => track.stop())

    if (videoRef.current) {
      videoRef.current.srcObject = null
    }

    setCameraStream(null)
  }

  const startCamera = async () => {
    setCameraError('')

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera is not supported on this browser or device.')
      return
    }

    try {
      stopCamera()

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      })

      setCameraStream(stream)

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }
    } catch (error) {
      console.error('Unable to start camera:', error)
      setCameraError('Unable to open the camera. Please check camera permission.')
    }
  }

  const startCaptureCountdown = () => {
    if (!cameraStream || captureCountdown > 0) return

    setCaptureCountdown(3)

    let nextCount = 3
    const timer = window.setInterval(() => {
      nextCount -= 1

      if (nextCount <= 0) {
        window.clearInterval(timer)
        setCaptureCountdown(0)
        captureCurrentPhoto()
        return
      }

      setCaptureCountdown(nextCount)
    }, 1000)
  }

  const captureCurrentPhoto = () => {
    const video = videoRef.current
    const canvas = canvasRef.current

    if (!video || !canvas) return

    const width = video.videoWidth || 1280
    const height = video.videoHeight || 720

    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')

    // Keep the captured image exactly like the live selfie preview.
    context.translate(width, 0)
    context.scale(-1, 1)
    context.drawImage(video, 0, 0, width, height)

    canvas.toBlob(
      (blob) => {
        if (!blob) return

        if (visitorPhoto?.preview) {
          URL.revokeObjectURL(visitorPhoto.preview)
        }

        const file = new File([blob], `visitor-photo-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        })

        setVisitorPhoto({
          file,
          preview: URL.createObjectURL(file),
          name: file.name,
        })

        stopCamera()
      },
      'image/jpeg',
      0.92,
    )
  }

  const removePhoto = () => {
    setCaptureCountdown(0)
    if (visitorPhoto?.preview) {
      URL.revokeObjectURL(visitorPhoto.preview)
    }

    setVisitorPhoto(null)
    startCamera()
  }

  useEffect(() => {
    if (step === 3 && !visitorPhoto) {
      startCamera()
    } else {
      stopCamera()
    }

    return () => {
      stopCamera()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  useEffect(() => {
    return () => {
      if (visitorPhoto?.preview) {
        URL.revokeObjectURL(visitorPhoto.preview)
      }

      stopCamera()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()

    // TODO: connect to backend API
    // const formData = new FormData()
    // formData.append('visitor_name', visitorName)
    // formData.append('phone', getFullPhoneNumber())
    // formData.append('area', selectedArea)
    // formData.append('category', category)
    // formData.append('subject', subject)
    // formData.append('priority', priority)
    // formData.append('description', description)
    // if (visitorPhoto?.file) formData.append('visitor_photo', visitorPhoto.file)

    navigate('/', { replace: true })
  }

  return (
    <main className="kiosk-page">
      <div className="app-container kiosk-container submit-ticket-page">
        <KioskHeader title="Submit Ticket" subtitle="Report an issue to the support team" />

        <div className="submit-ticket-hero">
          <div>
            <span className="submit-ticket-hero__eyebrow">
              <Ticket size={16} /> Support request
            </span>
            <h1 className="submit-ticket-hero__title">Submit Ticket</h1>
            <p className="submit-ticket-hero__subtitle">
              Create a support request with guided steps and clear information.
            </p>
          </div>

          <div className="submit-ticket-hero__status">
            <span>Step {step} of 4</span>
            <strong>{stepLabels[step - 1]}</strong>
          </div>
        </div>

        <div className="submit-ticket-layout">
          <form className="submit-ticket-main" onSubmit={handleSubmit}>
            <div className="submit-ticket-steps">
              {stepLabels.map((label, index) => {
                const number = index + 1

                return (
                  <button
                    key={label}
                    type="button"
                    className={`submit-ticket-step ${step === number ? 'is-active' : ''} ${
                      step > number ? 'is-done' : ''
                    }`}
                    onClick={() => goToStep(number)}
                  >
                    <span>{number}</span>
                    <strong>{label}</strong>
                  </button>
                )
              })}
            </div>

            {step === 1 && (
              <section className="submit-ticket-section">
                <div className="submit-ticket-section__header">
                  <h2>Select ticket category</h2>
                  <p>Choose the closest category so we can assign it to the right team.</p>
                </div>

                <div className="category-select-grid">
                  {CATEGORIES.map((item) => {
                    const Icon = item.icon

                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={`category-select-card ${
                          category === item.key ? 'is-selected' : ''
                        }`}
                        onClick={() => setCategory(item.key)}
                      >
                        <span className="category-select-card__icon">
                          <Icon size={24} />
                        </span>

                        <span>
                          <strong>{item.key}</strong>
                          <small>{item.desc}</small>
                        </span>
                      </button>
                    )
                  })}
                </div>

                <div className="submit-ticket-actions">
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={!canContinueStep1}
                    onClick={() => setStep(2)}
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                </div>
              </section>
            )}

            {step === 2 && (
              <section className="submit-ticket-section">
                <div className="submit-ticket-section__header">
                  <h2>Describe your issue</h2>
                  <p>Provide enough details so the support team can understand the issue.</p>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input
                      className="form-input"
                      value={visitorName}
                      onChange={(e) => setVisitorName(e.target.value)}
                      placeholder="Example: Martin"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <div className="phone-input phone-input--malaysia">
                      <div className="phone-input__prefix">🇲🇾 +60</div>
                      <input
                        type="tel"
                        inputMode="numeric"
                        className={`form-input phone-input__number ${phoneError ? 'is-error' : ''}`}
                        value={phone}
                        onChange={handlePhoneChange}
                        onBlur={handlePhoneChange}
                        placeholder="161234567"
                      />
                    </div>
                    {phoneError && <div className="form-error">{phoneError}</div>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Area</label>
                    <select
                      className="form-input"
                      value={area}
                      onChange={(e) => {
                        setArea(e.target.value)
                        if (e.target.value !== 'Other') setOtherArea('')
                      }}
                    >
                      <option value="">Select area</option>
                      {AREAS.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>

                  {area === 'Other' && (
                    <div className="form-group">
                      <label className="form-label">Other Area</label>
                      <input
                        className="form-input"
                        value={otherArea}
                        onChange={(e) => setOtherArea(e.target.value)}
                        placeholder="Please enter your area"
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Subject</label>
                    <input
                      className="form-input"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Example: Air conditioner not working"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Priority</label>
                    <div className="priority-pill-group">
                      {PRIORITIES.map((item) => (
                        <button
                          key={item}
                          type="button"
                          className={`priority-pill ${priority === item ? 'is-selected' : ''}`}
                          onClick={() => setPriority(item)}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-group form-group--full">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-textarea"
                      rows={7}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Please describe what happened, when it happened, and any important details."
                    />
                  </div>
                </div>

                <div className="submit-ticket-actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setStep(1)}>
                    <ArrowLeft size={16} /> Back
                  </button>

                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={!canContinueStep2}
                    onClick={() => setStep(3)}
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                </div>
              </section>
            )}

            {step === 3 && (
              <section className="submit-ticket-section">
                <div className="submit-ticket-section__header">
                  <h2>Visitor photo</h2>
                  <p>
                    Capture the visitor’s photo using the kiosk camera. A photo is required before
                    proceeding to review. File upload is disabled for public kiosk safety.
                  </p>
                </div>

                <div className="visitor-photo-box">
                  {visitorPhoto ? (
                    <div className="visitor-photo-preview">
                      <img src={visitorPhoto.preview} alt="Visitor preview" />

                      <button
                        type="button"
                        className="visitor-photo-remove"
                        onClick={removePhoto}
                        aria-label="Retake visitor photo"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="visitor-camera-preview">
                      <video
                        ref={videoRef}
                        className="visitor-camera-video"
                        autoPlay
                        muted
                        playsInline
                      />

                      {captureCountdown > 0 && (
                        <div className="visitor-camera-countdown">
                          <span>{captureCountdown}</span>
                          <strong>Get ready</strong>
                        </div>
                      )}

                      {!cameraStream && (
                        <div className="visitor-photo-empty">
                          <UserFocus size={42} />
                          <strong>Camera preview</strong>
                          <span>
                            {cameraError || 'The kiosk will open the camera directly in this box.'}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <canvas ref={canvasRef} hidden />

                  <div className="visitor-photo-controls">
                    {visitorPhoto ? (
                      <button
                        type="button"
                        className="btn btn--primary visitor-photo-button"
                        onClick={removePhoto}
                      >
                        <Camera size={18} />
                        Retake Photo
                      </button>
                    ) : cameraStream ? (
                      <button
                        type="button"
                        className="btn btn--primary visitor-photo-button"
                        onClick={startCaptureCountdown}
                        disabled={captureCountdown > 0}
                      >
                        <Camera size={18} />
                        {captureCountdown > 0 ? `Capturing in ${captureCountdown}` : 'Capture Photo'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--primary visitor-photo-button"
                        onClick={startCamera}
                      >
                        <Camera size={18} />
                        Open Camera
                      </button>
                    )}
                  </div>
                </div>

                <div className="submit-ticket-actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setStep(2)}>
                    <ArrowLeft size={16} /> Back
                  </button>

                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={!canContinueStep3}
                    onClick={() => setStep(4)}
                  >
                    Review <ArrowRight size={16} />
                  </button>
                </div>
              </section>
            )}

            {step === 4 && (
              <section className="submit-ticket-section">
                <div className="submit-ticket-section__header">
                  <h2>Review ticket</h2>
                  <p>Please check the details before submitting your ticket.</p>
                </div>

                <div className="review-card">
                  <div className="review-card__row">
                    <span>Name</span>
                    <strong>{visitorName || '-'}</strong>
                  </div>

                  <div className="review-card__row">
                    <span>Phone Number</span>
                    <strong>{getFullPhoneNumber() || '-'}</strong>
                  </div>

                  <div className="review-card__row">
                    <span>Area</span>
                    <strong>{selectedArea || '-'}</strong>
                  </div>

                  <div className="review-card__row">
                    <span>Category</span>
                    <strong>{category || '-'}</strong>
                  </div>

                  <div className="review-card__row">
                    <span>Subject</span>
                    <strong>{subject || '-'}</strong>
                  </div>

                  <div className="review-card__row">
                    <span>Priority</span>
                    <strong>{priority}</strong>
                  </div>

                  <div className="review-card__row">
                    <span>Visitor Photo</span>
                    <strong>{visitorPhoto ? 'Captured' : 'Not captured'}</strong>
                  </div>

                  {visitorPhoto && (
                    <div className="review-card__photo">
                      <img src={visitorPhoto.preview} alt="Visitor preview" />
                    </div>
                  )}

                  <div className="review-card__description">
                    <span>Description</span>
                    <p>{description || '-'}</p>
                  </div>
                </div>

                <div className="submit-ticket-actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setStep(3)}>
                    <ArrowLeft size={16} /> Back
                  </button>

                  <button type="submit" className="btn btn--primary">
                    <CheckCircle size={16} /> Submit Ticket
                  </button>
                </div>
              </section>
            )}
          </form>

          <aside className="submit-ticket-aside">
            <div className="aside-card aside-card--guide">
              <div className="aside-card__icon">
                <Info size={22} />
              </div>
              <h3>Ticket Guide</h3>
              <p>Select the correct category and provide clear details to help the team respond faster.</p>
            </div>

            <div className="aside-card">
              <h3>Current Selection</h3>

              <div className="aside-summary">
                <span>Category</span>
                <strong>{selectedCategory?.key || 'Not selected'}</strong>
              </div>

              <div className="aside-summary">
                <span>Name</span>
                <strong>{visitorName || 'Not entered'}</strong>
              </div>

              <div className="aside-summary">
                <span>Area</span>
                <strong>{selectedArea || 'Not selected'}</strong>
              </div>

              <div className="aside-summary">
                <span>Priority</span>
                <strong>{priority}</strong>
              </div>

              <div className="aside-summary">
                <span>Visitor Photo</span>
                <strong>{visitorPhoto ? 'Captured' : 'Required'}</strong>
              </div>
            </div>

            <div className="aside-card">
              <h3>Tips</h3>
              <div className="tip-list">
                <div>
                  <MapPin size={18} /> Include location if relevant.
                </div>
                <div>
                  <Camera size={18} /> Capture visitor photo for easier follow-up.
                </div>
                <div>
                  <WarningCircle size={18} /> File upload is disabled on public kiosk.
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  )
}